import { useEffect, useRef, useCallback } from 'react';
import { audioEngine } from '../lib/audio/AudioEngine';
import { calculateIntensity, isInDangerZone } from '../lib/audio/utils/audioUtils';
import type { ReplayState } from '../components/replay/ReplayEngine';

interface UseReplayAudioOptions {
  state: ReplayState | null;
  isMuted: boolean;
}

export function useReplayAudio({ state, isMuted }: UseReplayAudioOptions) {
  const isAmbientPlayingRef = useRef(false);
  const prevInDangerRef = useRef(false);
  const prevIsPlayingRef = useRef(false);
  const prevElapsedRef = useRef(0);

  // Initialize audio context
  const initializeAudio = useCallback(async () => {
    await audioEngine.initialize();
  }, []);

  // Handle play/pause state changes and cleanup when state is cleared
  useEffect(() => {
    // If state is null (replay closed), stop all audio
    if (!state) {
      audioEngine.stopAmbient();
      audioEngine.stopAlert();
      isAmbientPlayingRef.current = false;
      prevInDangerRef.current = false;
      prevIsPlayingRef.current = false;
      return;
    }

    const wasPlaying = prevIsPlayingRef.current;
    const isPlaying = state.isPlaying;

    // Started playing
    if (isPlaying && !wasPlaying && !isMuted) {
      audioEngine.startAmbient();
      isAmbientPlayingRef.current = true;
    }

    // Paused
    if (!isPlaying && wasPlaying) {
      audioEngine.stopAmbient();
      audioEngine.stopAlert();
      isAmbientPlayingRef.current = false;
      prevInDangerRef.current = false;
    }

    prevIsPlayingRef.current = isPlaying;
  }, [state, isMuted]);

  // Handle mute state changes
  useEffect(() => {
    if (!state) return;

    if (isMuted) {
      // Muted - stop all audio
      audioEngine.stopAmbient();
      audioEngine.stopAlert();
      isAmbientPlayingRef.current = false;
      prevInDangerRef.current = false;
    } else if (state.isPlaying) {
      // Unmuted while playing - start ambient
      audioEngine.startAmbient();
      isAmbientPlayingRef.current = true;
    }
  }, [isMuted, state?.isPlaying]);

  // Handle seek (large jumps in elapsed time)
  useEffect(() => {
    if (!state) return;

    const elapsed = state.elapsed;
    const prevElapsed = prevElapsedRef.current;
    const seekThreshold = 500; // 500ms jump indicates a seek

    // Detect seek (large time jump, either forward or backward)
    const isSeek = Math.abs(elapsed - prevElapsed) > seekThreshold;

    if (isSeek && !isMuted && state.isPlaying) {
      // Reset audio state on seek
      audioEngine.stopAlert();
      prevInDangerRef.current = false;
    }

    prevElapsedRef.current = elapsed;
  }, [state?.elapsed, isMuted, state?.isPlaying]);

  // Update ambient intensity based on replay progress
  useEffect(() => {
    if (!state || isMuted || !state.isPlaying || !isAmbientPlayingRef.current) return;

    const intensity = calculateIntensity(state.elapsed, state.circle);
    audioEngine.setAmbientIntensity(intensity);
  }, [state?.elapsed, state?.circle, isMuted, state?.isPlaying]);

  // Handle danger zone detection
  useEffect(() => {
    if (!state || isMuted) {
      audioEngine.stopAlert();
      prevInDangerRef.current = false;
      return;
    }

    if (!state.isPlaying) {
      audioEngine.stopAlert();
      prevInDangerRef.current = false;
      return;
    }

    const { circle, cursor, isInside } = state;
    if (!circle || !cursor) {
      audioEngine.stopAlert();
      prevInDangerRef.current = false;
      return;
    }

    // Check if in danger zone (outer 20% of circle) or outside
    const inDangerZone = isInDangerZone(cursor.x, cursor.y, circle);
    const inDanger = inDangerZone || !isInside;

    const wasInDanger = prevInDangerRef.current;

    if (inDanger && !wasInDanger) {
      // Just entered danger zone
      audioEngine.startAlert();
    } else if (!inDanger && wasInDanger) {
      // Back to safe zone
      audioEngine.stopAlert();
    }

    prevInDangerRef.current = inDanger;
  }, [state?.circle, state?.cursor, state?.isInside, state?.isPlaying, isMuted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioEngine.stopAmbient();
      audioEngine.stopAlert();
    };
  }, []);

  return {
    initializeAudio,
    isInitialized: audioEngine.isInitialized(),
  };
}
